import React, { useEffect, useState } from "react";
import { Edit2, Trash2, CheckCircle2, XCircle, Lock, Unlock } from "lucide-react";
import { toast } from "react-toastify";
import UserForm from "./UserForm";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import {
  activePremium,
  createUser,
  deleteUser,
  getUsers,
  uploadUsersFromExcel,
  downloadExcelTemplate,
  getMaxDevicesConfig,
  updateMaxDevicesConfig,
  toggleLockUser,
} from "../../services/UserService";
import { Tooltip } from "@mui/material";

const configDate = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
  timeZone: "Asia/Ho_Chi_Minh", // hoặc remove nếu dùng UTC
};

export default function Users() {
  // ]);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "",
    fullName: "",
    courses: [],
    vip: [],
    premium: false,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;
  const [isEditing, setIsEditing] = useState(null);
  const [listUsers, setListUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [excelFile, setExcelFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [maxDevices, setMaxDevices] = useState(2);
  const [savingMaxDevices, setSavingMaxDevices] = useState(false);
  const [filterPremium, setFilterPremium] = useState("ALL"); // ALL | TRUE | FALSE
  const [filterLocked, setFilterLocked] = useState("ALL"); // ALL | LOCKED | ACTIVE

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUsers(
          currentPage,
          limit,
          searchQuery,
          filterPremium,
          filterLocked
        );
        setListUsers(response?.data);
        setTotalPages(response?.totalPages);
        setCurrentPage(response?.currentPage);
      } catch (error) {
        const message = error?.response?.data?.message;
        toast.error(message);
      }
    };

    fetchUsers();
  }, [currentPage, searchQuery, filterPremium, filterLocked]);

  useEffect(() => {
    const fetchMaxDevices = async () => {
      try {
        const res = await getMaxDevicesConfig();
        if (res && typeof res.MAX_DEVICES === "number") {
          setMaxDevices(res.MAX_DEVICES);
        }
      } catch (error) {
        const message = error?.response?.data?.message || "Không lấy được MAX_DEVICES";
        toast.error(message);
      }
    };
    fetchMaxDevices();
  }, []);

  const handleEditUser = (user) => {
    setIsEditing(true);
    setFormData({
      ...user,
      courses: user.courses || [],
      vip: Array.isArray(user.vip) ? user.vip : user.vip ? [user.vip] : [],
      premium: user.premium || false,
    });
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDeleteUser = async () => {
    if (!userToDelete?._id) return;
    try {
      const res = await deleteUser(userToDelete._id);
      toast.success(res?.message);
      setListUsers(listUsers.filter((user) => user?._id !== userToDelete._id));
      handleCloseDeleteDialog();
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message);
    }
  };

  const handleSaveMaxDevices = async () => {
    const value = parseInt(maxDevices, 10);
    if (!Number.isInteger(value) || value <= 0 || value > 10) {
      toast.error("MAX_DEVICES phải là số nguyên từ 1 đến 10");
      return;
    }
    try {
      setSavingMaxDevices(true);
      const res = await updateMaxDevicesConfig(value);
      toast.success(res?.message || "Cập nhật MAX_DEVICES thành công");
      setMaxDevices(res?.MAX_DEVICES || value);
    } catch (error) {
      const message =
        error?.response?.data?.message || "Có lỗi khi cập nhật MAX_DEVICES";
      toast.error(message);
    } finally {
      setSavingMaxDevices(false);
    }
  };

  const handleInsertUser = async () => {
    if (validateForm()) {
      try {
        const res = await createUser(formData);
        if (res && res.data) {
          setListUsers([res.data, ...listUsers]);
          // Sau khi tạo user mới, reload danh sách từ trang 1
          setCurrentPage(1);
          toast.success(res?.message);
          setFormData({
            email: "",
            password: "",
            role: "",
            fullName: "",
            courses: [],
            vip: [],
            premium: false,
          });
        }
      } catch (error) {
        const message = error?.response?.data?.message;
        toast.error(message);
      }
    }
  };

  const handleUpdateUser = async () => {
    if (validateForm()) {
      try {
        const res = await createUser(formData);
        if (res && res.data) {
          setListUsers(
            listUsers.map((e) => (e._id === res.data?._id ? res.data : e))
          );
          // Sau khi cập nhật user, reload danh sách hiện tại
          setCurrentPage(1);
          toast.success(res?.message);
          setFormData({
            email: "",
            password: "",
            role: "",
            fullName: "",
            courses: [],
            vip: [],
            premium: false,
          });
        }
      } catch (error) {
        const message = error?.response?.data?.message;
        toast.error(message);
      }
    }
  };

  const validateForm = () => {
    if (!formData.email) {
      toast.error("Vui lòng nhập email");
      return false;
    }

    if (!formData.password && !isEditing) {
      toast.error("Vui lòng nhập password");
      return false;
    }

    if (!formData.fullName) {
      toast.error("Vui lòng nhập name");
      return false;
    }

    if (![0, 1].includes(formData.role)) {
      toast.error("Vui lòng nhập role");
      return false;
    }

    // Validate courses
    // if (!formData.courses || formData.courses.length === 0) {
    //   toast.error("Vui lòng chọn ít nhất một khóa học");
    //   return false;
    // }

    return true;
  };

  const handleChangeInputUser = (event) => {
    let { name, value } = event.target;
    if (name === "role") {
      const role = value === "Admin" ? 1 : 0;
      setFormData({
        ...formData,
        [name]: role,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = () => {
    // Reset về trang 1, useEffect sẽ tự fetch với searchQuery hiện tại
    setCurrentPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch(); // Trigger search when Enter is pressed
    }
  };

  const handleTogglePremium = async (id, premium) => {
    try {
      const res = await activePremium(id, !premium);
      toast.success(res.message);
      setListUsers(
        listUsers.map((user) =>
          user?._id === id
            ? {
              ...user,
              premium: !user?.premium,
            }
            : user
        )
      );
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message);
    }
  };

  const handleToggleLock = async (id, isLocked) => {
    try {
      const res = await toggleLockUser(id);
      toast.success(res?.message);
      setListUsers(
        listUsers.map((user) =>
          user?._id === id
            ? {
              ...user,
              isLocked: !isLocked,
            }
            : user
        )
      );
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message);
    }
  };

  // Helper function to render courses badges
  const renderCourses = (courses) => {
    if (!courses || courses.length === 0) {
      return <span className="text-gray-400 text-sm">Chưa có khóa học</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {courses.map((course, index) => (
          <span
            key={index}
            className={`px-2 py-1 rounded-full text-xs font-medium ${course === "TSA"
              ? "bg-blue-100 text-blue-800"
              : "bg-green-100 text-green-800"
              }`}
          >
            {course}
          </span>
        ))}
      </div>
    );
  };

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      const validExtensions = [".xlsx", ".xls"];
      const fileExtension = file.name.substring(file.name.lastIndexOf("."));

      if (
        !validTypes.includes(file.type) &&
        !validExtensions.includes(fileExtension)
      ) {
        toast.error("Vui lòng chọn file Excel (.xlsx hoặc .xls)");
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error("File không được vượt quá 10MB");
        return;
      }

      setExcelFile(file);
    }
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      toast.error("Vui lòng chọn file Excel");
      return;
    }

    try {
      setUploading(true);
      const response = await uploadUsersFromExcel(excelFile);
      if (response && response.data) {
        const { created, updated, errors } = response.data;
        toast.success(
          `Upload thành công! Đã tạo: ${created}, Cập nhật: ${updated}`
        );
        if (errors && errors.length > 0) {
          console.error("Lỗi khi upload:", errors);
          toast.warning(`Có ${errors.length} lỗi xảy ra. Vui lòng kiểm tra console.`);
        }
        setExcelFile(null);
        const fileInput = document.getElementById("excel-file-input");
        if (fileInput) {
          fileInput.value = "";
        }
        // Refresh user list sau khi upload
        setCurrentPage(1);
      }
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message || "Có lỗi xảy ra khi upload file Excel");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      await downloadExcelTemplate();
      toast.success("Tải file mẫu thành công!");
    } catch (error) {
      toast.error("Có lỗi xảy ra khi tải file mẫu");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-[30px] overflow-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">
        Quản Lý Người Dùng
      </h2>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Xác nhận xóa</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Bạn có chắc chắn muốn xóa người dùng{" "}
            <strong>{userToDelete?.fullName}</strong> ({userToDelete?.email})?
            Hành động này không thể hoàn tác.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Hủy
          </Button>
          <Button onClick={handleConfirmDeleteUser} color="error" variant="contained" autoFocus>
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* MAX_DEVICES Config */}
      <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-md font-semibold text-gray-800">
            Giới hạn số thiết bị đăng nhập (MAX_DEVICES)
          </h3>
          <p className="text-xs text-gray-600">
            Mỗi tài khoản chỉ được đăng nhập tối đa N thiết bị đồng thời. Khi vượt quá, tài khoản sẽ bị khóa.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="1"
            max="10"
            value={maxDevices}
            onChange={(e) => setMaxDevices(e.target.value)}
            className="w-20 px-2 py-1 border rounded-md text-center"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveMaxDevices}
            disabled={savingMaxDevices}
          >
            {savingMaxDevices ? "Đang lưu..." : "Lưu"}
          </Button>
        </div>
      </div>

      {/* user Form */}
      <UserForm
        isEditing={isEditing}
        formData={formData}
        handleInsertUser={handleInsertUser}
        handleChangeInputUser={handleChangeInputUser}
        handleUpdateUser={handleUpdateUser}
      />
      {/* Excel Upload Section */}
      <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-3 text-gray-800">
          Upload Excel để tạo nhiều tài khoản
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          File Excel cần có các cột: email, password, fullName, courses
          (TSA,THPT), premium (true/false), role (0/1), vip (VIP1,VIP2,VIP3,VIP4 - nhiều VIP cách nhau bằng dấu phẩy)
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="outlined"
            onClick={handleDownloadTemplate}
            disabled={downloading}
            startIcon={<DownloadIcon />}
            sx={{
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": {
                borderColor: "#1565c0",
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              },
            }}
          >
            {downloading ? "Đang tải..." : "Tải file mẫu"}
          </Button>
          <Button
            variant="outlined"
            component="label"
            disabled={uploading}
            startIcon={<CloudUploadIcon />}
          >
            Chọn file Excel
            <input
              id="excel-file-input"
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleExcelFileChange}
            />
          </Button>
          {excelFile && (
            <span className="text-sm text-gray-600">{excelFile.name}</span>
          )}
          <Button
            variant="contained"
            onClick={handleExcelUpload}
            disabled={!excelFile || uploading}
            startIcon={<CloudUploadIcon />}
          >
            {uploading ? "Đang upload..." : "Upload Excel"}
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            placeholder="Tìm kiếm người dúng.."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress} // Listen for Enter key
            className="w-96 px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-md"
          >
            Search
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">Premium:</span>
          <select
            value={filterPremium}
            onChange={(e) => {
              setFilterPremium(e.target.value);
              setCurrentPage(1);
            }}
            className="px-2 py-1 border rounded-md text-sm"
          >
            <option value="ALL">Tất cả</option>
            <option value="TRUE">Chỉ TRUE</option>
            <option value="FALSE">Chỉ FALSE</option>
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">Locked:</span>
          <select
            value={filterLocked}
            onChange={(e) => {
              setFilterLocked(e.target.value);
              setCurrentPage(1);
            }}
            className="px-2 py-1 border rounded-md text-sm"
          >
            <option value="ALL">Tất cả</option>
            <option value="LOCKED">Chỉ LOCKED</option>
            <option value="ACTIVE">Chỉ ACTIVE</option>
          </select>
        </div>
      </div>

      {/* user Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          {" "}
          {/* Thêm cuộn dọc nếu có quá nhiều hàng */}
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="p-3 text-left">No</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Full name</th>
                <th className="p-3 text-left">Role</th>
                <th className="p-3 text-left">Courses</th>
                <th className="p-3 text-left">VIP</th>
                <th className="p-3 text-left">Premium</th>
                <th className="p-3 text-left">Locked</th>
                <th className="p-3 text-left">Created At</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listUsers.length > 0 &&
                listUsers.map((user, index) => (
                  <tr
                    key={user?._id}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <Tooltip title={user?._id} placement="top">
                      <td className="p-3">
                        {" "}
                        {user?._id?.slice(0, 5)}...{user?._id?.slice(-5)}
                      </td>
                    </Tooltip>
                    <td className="p-3">{user?.email}</td>
                    <td className="p-3">{user?.fullName}</td>
                    <td className="p-3">
                      {user?.role === 1 ? "Admin" : "User"}
                    </td>
                    <td className="p-3">{renderCourses(user?.courses)}</td>
                    <td className="p-3">
                      {user?.vip && (Array.isArray(user.vip) ? user.vip.length : 1) > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {(Array.isArray(user.vip) ? user.vip : [user.vip]).map((v, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                            >
                              {v}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${user?.premium === true
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                          }`}
                      >
                        {user?.premium === true ? "TRUE" : "FALSE"}
                      </span>
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${user?.isLocked
                          ? "bg-red-100 text-red-800"
                          : "bg-green-100 text-green-800"
                          }`}
                      >
                        {user?.isLocked ? "LOCKED" : "ACTIVE"}
                      </span>
                    </td>
                    <td className="p-3">
                      {new Date(user?.createdAt).toLocaleDateString(
                        "vi-VN",
                        configDate
                      )}
                    </td>

                    <td className="p-3">
                      <div className="flex items-center justify-center space-x-2 h-full min-h-[40px]">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-blue-500 hover:text-blue-700 transition"
                          title="Edit User"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(user)}
                          className="text-red-500 hover:text-red-700 transition"
                          title="Delete user"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() =>
                            handleTogglePremium(user?._id, user?.premium)
                          }
                          className={`${user?.premium === true
                            ? "text-red-500 hover:text-red-700"
                            : "text-green-500 hover:text-green-700"
                            } transition`}
                          title={
                            user?.premium === true ? "Deactivate" : "Activate"
                          }
                        >
                          {user?.premium === true ? (
                            <XCircle className="w-5 h-5" />
                          ) : (
                            <CheckCircle2 className="w-5 h-5" />
                          )}
                        </button>
                        <button
                          onClick={() =>
                            handleToggleLock(user?._id, user?.isLocked)
                          }
                          className={`${user?.isLocked
                            ? "text-orange-500 hover:text-orange-700"
                            : "text-gray-500 hover:text-gray-700"
                            } transition`}
                          title={
                            user?.isLocked ? "Mở khóa tài khoản" : "Khóa tài khoản"
                          }
                        >
                          {user?.isLocked ? (
                            <Unlock className="w-5 h-5" />
                          ) : (
                            <Lock className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between p-4 items-center">
          <span>
            Page {`${currentPage} / ${totalPages}`}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((p) => (p < totalPages ? p + 1 : p))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
